import { Injectable, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, Subject, Subscription, TeardownLogic, take, map } from 'rxjs';

import { addSeconds, differenceInSeconds, isValid } from 'date-fns';
import { SessionStorage } from 'ngx-webstorage';

import {
    ConfirmationDialogOptions,
    ConfirmationDialogService,
    MessageBoxDialogService,
    SplashService,
} from '@polk_county_public_schools/ngx-pcps-dialogs';

import { ConfigService } from '../config/config.service';
import { UserInfo, UserService } from '../user/user.service';
import { VERSION_NUMBER, BUILD_TIME } from '../version/version.service';

import { AuthTokenRefreshResult, LoginResult, RevalidationInfo, TestConnectionInfo } from 'services/authentication/auth.service.class';
import { UnsavedChangesService } from 'routes/routes.guard';
import { UserRoleInfo } from 'services/user/user.service.class';
import { SystemRole } from 'modules/shared/roles';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // 40 minutes (time period to wait between successive checks of whether the auth token is recently used)
    static AUTHCHECKTIME: number = 40 * 60;
    // 2 minutes, 50 seconds (time period to wait before logging the user out automatically). Roughly 42 minutes total between check
    // and autologout
    static LOGOUTWARNINGTIME = 170;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    @SessionStorage(undefined, null) nextAuthRefresh!: string | null;
    @SessionStorage(undefined, false) private systemOnline = false;

    AuthTokenCancelFlag: boolean;
    AuthTokenRefreshHandle: number | null;
    AuthRefreshDeadline: Date | null;
    HasVersionUpdate: Subject<boolean> = new Subject<boolean>();
    SystemOnlineSubject: Subject<TestConnectionInfo> = new Subject<TestConnectionInfo>();
    User: UserInfo | null = null;
    VersionNumber: Subject<string> = new Subject<string>();
    IsRefreshing: boolean;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private datePipe: DatePipe,
        private confdialog: ConfirmationDialogService,
        private msgBoxSvc: MessageBoxDialogService,
        private unsavedChangesSvc: UnsavedChangesService,
        private userService: UserService,
        private router: Router,
        private splashService: SplashService
    ) {
        this.AuthTokenCancelFlag = false;
        this.AuthTokenRefreshHandle = null;
        this.AuthRefreshDeadline = this.nextAuthRefresh ? new Date(this.nextAuthRefresh) : null;
        this.IsRefreshing = false;

        const btime = new Date(BUILD_TIME);

        this.VersionNumber.next(VERSION_NUMBER);

        this.SystemOnlineSubject.next({
            SystemMessage: '',
            Status: this.systemOnline,
            EProfVersion: VERSION_NUMBER,
        });

        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this.subrelease = this.userService.UserSubject.subscribe((user) => {
            this.User = user;

            // See if we have a User object already known to us. If so, this was a refresh of the page most likely,
            // and thus we need to kick start authtoken refreshing again.
            if (this.User?.AuthToken && !this.IsRefreshing) {
                this.StartAuthTokenRefreshing();
            }
        });
    }

    ngOnDestroy(): void {
        this.CancelAuthTokenRefreshing();
        this._subrelease.unsubscribe();
    }

    // #region Auth Token Refresh Method

    private AuthTokenRefresh(): void {
        const self = this;

        // Now retrieve the auth token for the currently logged in user, stored in the userService
        const auth = self.User?.AuthToken;

        // Call the system refresh routine.
        self.RefreshAuthToken().subscribe({
            next: (authresult) => {
                // If no token comes up, the user got logged out somehow.
                if (!authresult || !authresult.AuthToken) {
                    // self.OpenMessageBoxDialog({
                    self.msgBoxSvc
                        .Open({
                            TitleText: 'Connection Lost',
                            MessageText:
                                'Your connection to the system has been broken. You will need to login again to use the system.',
                            OKText: 'OK',
                        })
                        .subscribe((_) => {
                            self.Logout();

                            // Redirect to login.
                            self.router.navigate(['/login']);
                        });
                } else {
                    // Update their token.
                    self.userService.UpdateAuthToken(authresult.AuthToken);

                    // Set the session variable as well.
                    self.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
                    self.nextAuthRefresh = self.AuthRefreshDeadline.toISOString();

                    // Reset for the next auth refresh cycle.
                    self.AuthTokenRefreshHandle = window.setTimeout(() => {
                        self.AuthTokenRefresh();
                    }, AuthService.AUTHCHECKTIME * 1000);
                }
            },
            error: (err) => {
                this.HandleServiceError(err);
            },
        });
    }

    // #endregion

    // #region Cancel Auth Token Refreshing Method

    CancelAuthTokenRefreshing(): void {
        // If possible, use the handle to cancel the timeout from happening
        if (this.AuthTokenRefreshHandle != null) {
            window.clearTimeout(this.AuthTokenRefreshHandle);
        }

        // Cancel the flag, handle, and deadline to refresh
        this.AuthTokenRefreshHandle = null;
        this.AuthTokenCancelFlag = false;
        this.AuthRefreshDeadline = null;
        this.IsRefreshing = false;
        this.nextAuthRefresh = null;
    }

    // #endregion

    // #region Change Impersonation Target Service Call

    ChangeImpersonationTarget(AuthToken: string, SAPID: string): Observable<LoginResult> {
        return this.http.get<LoginResult>(
            `${this.configService.hostAddress}` +
                `Api/Services/Authorization/ChangeImpersonationTarget?AuthToken=${AuthToken || ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Check Recent Use Service Method - This method simply calls the database to see if the user has done anything

    CheckRecentUse(AuthToken: string): Observable<boolean> {
        return this.http.get<boolean>(
            this.configService.hostAddress + `Api/Services/Authentication/CheckRecentUse?AuthToken=${AuthToken || ''}`
        );
    }

    // #endregion

    // #region Classlink SSO Login Service Call

    ClasslinkSSOLogin(token: string, screen?: string, winres?: string) {
        return this.http.post<UserInfo>(this.configService.hostAddress + 'api/Login/classlink', {
            Token: token,
            Screen: screen,
            Window: winres,
        });
    }

    // #endregion

    // #region Convert to User Info

    ConvertToUserInfo(logininfo: LoginResult): UserInfo {
        const self = this;

        const { Roles, ...infopacket } = logininfo;

        // Convert Roles into proper UserRoleInfo objects.
        const uRoles: UserRoleInfo[] =
            Roles?.map((r) => {
                return {
                    IsDeveloper: r.IsDeveloper,
                    IsSystemAdmin: r.IsSystemAdmin,
                    IsDistrictAdmin: r.IsDistrictAdmin,
                    IsRegional: r.IsRegional,
                    IsBoard: r.IsBoard,
                    IsPrincipalUpdater: r.IsPrincipalUpdater,
                    IsPrincipal: r.IsPrincipal,
                    WorkLocation: r.WorkLocation,
                    RoleName: self.GetRolesEnum(r.RoleName),
                } as UserRoleInfo;
            }) || [];

        const newUser: UserInfo = { ...infopacket, ...{ Roles: uRoles } };

        return newUser;
    }

    // #endregion

    // #region Get Roles Enum

    GetRolesEnum(rolename: string): SystemRole {
        // tslint:disable-next-line: no-any triple-equals
        const keys = Object.keys(SystemRole).filter((k) => (SystemRole as any)[k] == rolename);

        // tslint:disable-next-line: no-any
        return keys.length > 0 ? (SystemRole as any)[keys[0]] : null;
    }

    // #endregion

    // #region Handle Service Error Service Call

    // tslint:disable-next-line: no-any
    HandleServiceError(error: any): void {
        let status: number | null = null;
        let msg = '';

        if (error instanceof HttpErrorResponse) {
            status = error.status;

            const body = error && error.error;
            msg = body.ExceptionMessage || body.Message || body || 'An unknown error has occurred.';
        } else {
            let errorPacket = error.message || error;
            if (errorPacket != null && errorPacket !== '') {
                try {
                    errorPacket = JSON.parse(errorPacket);
                } catch {
                    errorPacket = errorPacket;
                }
            }
            msg =
                errorPacket != null && errorPacket instanceof Object
                    ? errorPacket.ExceptionMessage || errorPacket.Message || error
                    : errorPacket || 'An unknown error has occurred.';
        }

        this.splashService.CloseSplash();

        // Something else, which should be snackbar displayed.
        this.splashService.SnackbarError(msg, 8000);
    }

    // #endregion

    // #region Logout From System Service Call

    LogoutFromSystem(): Observable<boolean> {
        return this.http.put<boolean>(this.configService.hostAddress + `api/Login`, {});
    }

    // #endregion

    // #region Logout Service Call

    Logout(confirmation?: boolean, reason?: number): void {
        const self = this;
        let confObs: Observable<boolean>;

        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            const opts: ConfirmationDialogOptions = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            // confObs = self.OpenConfirmationDialog(opts);
            confObs = self.confdialog.Open(opts);
        } else {
            // No dialog needed.
            confObs = of<boolean>(true).pipe(take(1));
        }

        confObs.subscribe((result) => {
            if (result === true) {
                // User is opting to log out. Proceed.
                self.LogoutFromSystem().subscribe();

                // Stop authtoken refreshing
                self.CancelAuthTokenRefreshing();

                // Null out the current user.
                self.userService.ClearUser();

                // Snackbar the logout if this was user initiated (reason null or 0)
                if (reason == null || reason === 0) {
                    self.splashService.SnackbarSuccess('Successfully logged out.', 5000);
                }

                // Close out all remaining open dialogs
                // self.matDialog.closeAll();
                self.confdialog.CloseAllDialogs();

                // Clear out change tracking.
                self.unsavedChangesSvc.ClearTracking();

                // Navigate to the login state.
                self.router.navigate(['/login']);
                // window.location.href = self.configService.hostAddress;
            }
        });
    }

    // #endregion

    // #region Process Login

    ProcessLogin(logininfo: LoginResult): void {
        const self = this;

        // Convert the LoginResult object to a UserInfo object.
        const userinfo = self.ConvertToUserInfo(logininfo);

        // Set the currently logged in user information.
        self.userService.SetUser(userinfo);

        if (userinfo?.Roles?.length) {
            self.userService.SetUserCurrentRole(userinfo.Roles[0]);
        } else {
            // No roles found. Reject politely.
            self.msgBoxSvc
                .Open({
                    TitleText: 'Unable to Login',
                    MessageText: `We're sorry, the PCPS Standards Based Instruction (SBI) system is current only available to limited district leadership, designated district users, and school-based administrators. If you believe this is message is in error, please contact the district instructional office for more assistance.`,
                })
                .subscribe((_) => {
                    self.Logout(undefined, undefined);
                });
        }
    }

    // #endregion

    // #region Refresh Authentication Token Service Method

    RefreshAuthToken(): Observable<AuthTokenRefreshResult> {
        return this.http.put<AuthTokenRefreshResult>(this.configService.hostAddress + `api/Login/refresh`, {});
    }

    // #endregion

    // #region Reset User Password Service Call

    ResetUserPassword(password: string, valtoken: string): Observable<string> {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/Authentication/ResetUserPassword?Password=${password || ''}&ValToken=${valtoken || ''}`
        );
    }

    // #endregion

    // #region Revalidate Session Service Call

    RevalidateSession(Username: string, Password: string, ImpersonatedUsername: string): Observable<RevalidationInfo> {
        return this.http
            .put<UserInfo>(this.configService.hostAddress + `api/Login/revalidate`, {
                Username: Username || '',
                Password: Password || '',
                ImpersonatedUsername: ImpersonatedUsername || '',
            })
            .pipe(
                map((data) => {
                    return {
                        Error: null,
                        UserInfo: data,
                    } as RevalidationInfo;
                })
            );
    }

    // #endregion

    // #region Set System Online Status

    SetSystemOnlineStatus(status: TestConnectionInfo): void {
        this.systemOnline = status?.Status || false;
        this.SystemOnlineSubject.next(status);
    }

    // #endregion

    // #region Start AuthToken Refreshing Service Call

    StartAuthTokenRefreshing(): void {
        // This process will work as follows: We will call a service method which will tell us if the current user's auth token shows
        // "recent use" (which is a flag set by most stored procedures). If that method succeeds, and shows recent use, we will simply
        // use a different service call to update the auth token refresh time, then delay for a time period (AUTHCHECKTIME) before we
        // run this same check again.
        // If at any point the recent use flag is not set, then we set a timer to logout the user after a set amount of time
        // (LOGOUTWARNINGTIME), and simultaneously put up a dialog box that gives the user a visual representation of the countdown,
        // along with a button they can click to "save" their session, which essentially manually triggers the refresh method.

        // Initiate the auth token refresh cycling by calling the refresh method the first time
        this.AuthTokenCancelFlag = false;

        // Check real quick for a present value for AuthRefreshDeadline. If that exists, then it indicates the time when we absolutely
        // need to refresh the auth token, so we should set the refresh handle time to just before then. Otherwise, this is probably a
        // first login so use the default time.
        let timeToRefresh = AuthService.AUTHCHECKTIME;

        if (this.nextAuthRefresh) {
            // Read the deadline time to refresh from the session storage, convert into a Date object.
            this.AuthRefreshDeadline = new Date(this.nextAuthRefresh);
        } else {
            // Nothing available from session storage, so queue up a fresh refresh clock.
            this.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
        }

        if (this.AuthRefreshDeadline && isValid(this.AuthRefreshDeadline)) {
            // Find the time difference in seconds between now and when we are due to refresh.
            const timediff = differenceInSeconds(this.AuthRefreshDeadline, new Date());

            // We will always use the lesser of the next known time to refresh and the usual delay time. This is in case we are refreshing
            // the browser and need to restart refresh cycle, as opposed to fresh starting cycles.
            if (timediff < timeToRefresh) {
                timeToRefresh = timediff;
            }
            if (timeToRefresh <= 0) {
                timeToRefresh = 3;
            } // Ensure at least a 3 second wait.
        }

        this.AuthTokenRefreshHandle = window.setTimeout(() => {
            this.AuthTokenRefresh();
        }, timeToRefresh * 1000);

        // Set the refresh deadline app value
        const Now = new Date();

        this.AuthRefreshDeadline = addSeconds(Now, timeToRefresh);
        this.IsRefreshing = true;
        this.nextAuthRefresh = this.AuthRefreshDeadline.toISOString();
    }

    // #endregion

    // #region Test Connection Service Call

    TestConnection(): Observable<TestConnectionInfo> {
        // Call the WebApi to see if anyone is home.
        return this.http.get<TestConnectionInfo>(this.configService.hostAddress + 'api/Login/testconnection');
    }

    // #endregion

    // #region Validate User

    ValidateUser(
        username: string,
        password: string,
        impersonatedUser: string,
        training: boolean,
        screen: string,
        window: string
    ): Observable<UserInfo> {
        return this.http.post<UserInfo>(this.configService.hostAddress + `api/Login/loginuser`, {
            Username: username || '',
            Password: password || '',
            TrainingMode: training || false,
            ImpersonatedUsername: impersonatedUser || '',
            Screen: screen || '',
            Window: window || '',
        });
    }

    // #endregion
}
